import React, { createContext, useContext, useState } from 'react';
import { getNumDaysSinceEpoch } from '../../utils/utils.js';
import { hasSeenNotification, hasLoadedWeaverX, hasLoadedSubtext } from '../../utils/userStatsUtils.ts';
import { WEAVER__X_START_DAY_SINCE_EPOCH } from '../../utils/wordPairsX.ts';
import { SUBTEXT_START_DAY_SINCE_EPOCH } from '../../utils/subtextGameData.ts';

interface NotificationContextType {
  hasSidebarNotification: boolean;
  setHasSidebarNotification: (hasNotification: boolean) => void;
  showWeaverXLaunchNotification: boolean;
  setShowWeaverXLaunchNotification: (show: boolean) => void;
  showSubtextLaunchNotification: boolean;
  setShowSubtextLaunchNotification: (show: boolean) => void;
  showArchiveLaunchNotification: boolean;
  setShowArchiveLaunchNotification: (show: boolean) => void;
  hasAnyNotification: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Weaver X notification configuration
const weaverXNotificationConfig = {
  id: 'weaver-x-launch',
  startDay: WEAVER__X_START_DAY_SINCE_EPOCH,
  endDay: WEAVER__X_START_DAY_SINCE_EPOCH + 13,
};

// Subtext notification configuration
const subtextNotificationConfig = {
  id: 'subtext-launch',
  startDay: SUBTEXT_START_DAY_SINCE_EPOCH,
  endDay: SUBTEXT_START_DAY_SINCE_EPOCH + 13,
};

// Archive feature notification configuration
const ARCHIVE_LAUNCH_DAY = 20534; // 2026-03-22
const archiveNotificationConfig = {
  id: 'archive-launch',
  startDay: ARCHIVE_LAUNCH_DAY,
  endDay: ARCHIVE_LAUNCH_DAY + 13,
};

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [hasSidebarNotification, setHasSidebarNotification] = useState(false);
  
  // Weaver X launch notification logic
  function shouldShowWeaverXLaunchNotification() {
    const today = getNumDaysSinceEpoch();
    return (
      !hasSeenNotification(weaverXNotificationConfig.id) &&
      !hasLoadedWeaverX() &&
      today >= weaverXNotificationConfig.startDay &&
      today <= weaverXNotificationConfig.endDay
    );
  }
  const [showWeaverXLaunchNotification, setShowWeaverXLaunchNotification] = useState(shouldShowWeaverXLaunchNotification());

  // Subtext launch notification logic
  function shouldShowSubtextLaunchNotification() {
    const today = getNumDaysSinceEpoch();
    return (
      !hasSeenNotification(subtextNotificationConfig.id) &&
      !hasLoadedSubtext() &&
      today >= subtextNotificationConfig.startDay &&
      today <= subtextNotificationConfig.endDay
    );
  }
  const [showSubtextLaunchNotification, setShowSubtextLaunchNotification] = useState(shouldShowSubtextLaunchNotification());

  // Archive launch notification logic
  function shouldShowArchiveLaunchNotification() {
    const today = getNumDaysSinceEpoch();
    return (
      !hasSeenNotification(archiveNotificationConfig.id) &&
      today >= archiveNotificationConfig.startDay &&
      today <= archiveNotificationConfig.endDay
    );
  }
  const [showArchiveLaunchNotification, setShowArchiveLaunchNotification] = useState(shouldShowArchiveLaunchNotification());

  // Combine all notification states
  const hasAnyNotification = hasSidebarNotification || showWeaverXLaunchNotification || showSubtextLaunchNotification || showArchiveLaunchNotification;

  return (
    <NotificationContext.Provider value={{
      hasSidebarNotification,
      setHasSidebarNotification,
      showWeaverXLaunchNotification,
      setShowWeaverXLaunchNotification,
      showSubtextLaunchNotification,
      setShowSubtextLaunchNotification,
      showArchiveLaunchNotification,
      setShowArchiveLaunchNotification,
      hasAnyNotification,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
} 